import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Copy,
  Check,
  ExternalLink,
  Sparkles,
  Zap,
  MessageSquare,
  Link2,
  Youtube,
  PlayCircle
} from 'lucide-react';

const ARIAONE_PRODUCTS = [
  {
    id: 'lumina',
    name: 'Lumina',
    description: 'AI-powered clinical documentation assistant that reduces charting time by up to 70%. Lumina listens to patient encounters and generates comprehensive clinical notes automatically.',
    icon: Sparkles,
    color: '#8B5CF6',
    bgColor: '#8B5CF6/10',
    link: 'https://ariaone.ai/lumina',
    features: ['Voice-to-note AI', 'Multi-specialty templates', 'EHR integration']
  },
  {
    id: 'revq',
    name: 'RevQ',
    description: 'Intelligent revenue cycle optimization platform. RevQ analyzes claims data, identifies denial patterns, and provides actionable insights to maximize reimbursement.',
    icon: Zap,
    color: '#0071E3',
    bgColor: '#0071E3/10',
    link: 'https://ariaone.ai/revq',
    features: ['Claims analytics', 'Denial prevention', 'Revenue forecasting']
  },
  {
    id: 'echo',
    name: 'Echo',
    description: 'Patient engagement and communication platform. Echo automates appointment reminders, follow-ups, and patient outreach with AI-personalized messaging.',
    icon: MessageSquare,
    color: '#10B981',
    bgColor: '#10B981/10',
    link: 'https://ariaone.ai/echo',
    features: ['Automated outreach', 'Multi-channel messaging', 'Patient satisfaction tracking']
  }
];

const Referrals = () => {
  const { user } = useAuth();
  const [copiedLink, setCopiedLink] = useState(null);

  // Generate referral link with user's ID/email as tracking parameter
  const getReferralLink = (product) => {
    const referralCode = user?.id || user?.email?.split('@')[0] || 'agent';
    return `${product.link}?ref=${encodeURIComponent(referralCode)}`;
  };

  const handleCopyLink = async (productId, link) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedLink(productId);
      toast.success('Referral link copied to clipboard!');
      setTimeout(() => setCopiedLink(null), 3000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  return (
    <div className="space-y-6" data-testid="referrals-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-[#1B365D] tracking-tight">Partner Referrals</h1>
        <p className="text-slate-500 mt-1">Share AriaOne products with your network and earn referral bonuses</p>
      </div>

      {/* AriaOne Partner Banner */}
      <Card className="border-0 shadow-apple bg-gradient-to-r from-[#1B365D] to-[#0071E3] text-white overflow-hidden" data-testid="ariaone-banner">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <Link2 className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold flex items-center gap-2">
                  AriaOne Partner Program
                  <Badge className="bg-white/20 text-white border-0 text-xs">Official Partner</Badge>
                </h3>
                <p className="text-white/80 text-sm mt-1 max-w-xl">
                  AriaOne is an AI ecosystem of apps for healthcare providers. As an Enable Healthcare agent, 
                  you have exclusive access to share these products with your network. Each referral you make 
                  is tracked and eligible for bonuses.
                </p>
              </div>
            </div>
            <Button 
              asChild
              className="bg-white text-[#1B365D] hover:bg-white/90 rounded-full shadow-lg whitespace-nowrap"
              data-testid="ariaone-website-btn"
            >
              <a href="https://ariaone.ai" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4 mr-2" />
                Visit AriaOne
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Product Referral Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {ARIAONE_PRODUCTS.map((product, index) => {
          const Icon = product.icon;
          const referralLink = getReferralLink(product);
          const isCopied = copiedLink === product.id;
          
          return (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="border-0 shadow-apple h-full flex flex-col" data-testid={`product-card-${product.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${product.color}15` }}
                    >
                      <Icon className="w-6 h-6" style={{ color: product.color }} />
                    </div>
                    <a href={product.link} target="_blank" rel="noopener noreferrer">
                      <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600">
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </a>
                  </div>
                  <CardTitle className="font-heading text-xl mt-3">{product.name}</CardTitle>
                  <CardDescription className="text-slate-600">
                    {product.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  {/* Features */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {product.features.map(feature => (
                      <Badge key={feature} variant="secondary" className="text-xs">
                        {feature}
                      </Badge>
                    ))}
                  </div>
                  
                  {/* Referral Link Section */}
                  <div className="mt-auto space-y-3">
                    <label className="text-sm font-medium text-slate-700">Your Referral Link</label>
                    <div className="flex gap-2">
                      <Input 
                        value={referralLink}
                        readOnly
                        className="text-sm bg-slate-50 font-mono"
                        data-testid={`referral-link-${product.id}`}
                      />
                      <Button 
                        variant={isCopied ? "default" : "outline"}
                        className={`flex-shrink-0 rounded-full ${isCopied ? 'bg-green-600 hover:bg-green-700' : ''}`}
                        onClick={() => handleCopyLink(product.id, referralLink)}
                        data-testid={`copy-btn-${product.id}`}
                      >
                        {isCopied ? (
                          <>
                            <Check className="w-4 h-4 mr-2" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4 mr-2" />
                            Copy
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* YouTube Channel Banner */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-red-600 to-red-500 text-white overflow-hidden" data-testid="ariaone-youtube-banner">
        <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <Youtube className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-medium">Watch & Learn About AriaOne Apps</p>
              <p className="text-white/80 text-sm">Visit AriaOne's YouTube channel to watch demo videos and tutorials</p>
            </div>
          </div>
          <Button 
            asChild
            className="bg-white text-red-600 hover:bg-white/90 rounded-full shadow-lg whitespace-nowrap flex-shrink-0"
            data-testid="ariaone-youtube-btn"
          >
            <a href="https://www.youtube.com/@ariaoneai" target="_blank" rel="noopener noreferrer">
              <PlayCircle className="w-4 h-4 mr-2" />
              Watch Videos
            </a>
          </Button>
        </CardContent>
      </Card>

      {/* Info Note */}
      <Card className="border-0 bg-slate-50 shadow-sm">
        <CardContent className="p-4 flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-[#1B365D]/10 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-[#1B365D]" />
          </div>
          <div>
            <p className="font-medium text-slate-900">How Referrals Work</p>
            <p className="text-sm text-slate-600 mt-1">
              When you share your referral link and someone signs up for an AriaOne product, your referral is tracked automatically. 
              Contact your Enable Healthcare manager for information about current referral bonuses and commission structures.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Referrals;
